// utils/payment.ts

import { connectMindPaths, preciousChildPaths } from 'constants/path';
import { NavigateFunction } from 'react-router-dom';

// 타입 경로는 프로젝트에 맞게 조정
import { PaymentResult } from 'models/MindUp/purchaseCounselingApiTypes';
import { mindUpHolidayApi } from 'api/MindUp/mindUpHolidayApi';

declare global {
    interface Window {
        SETTLE_PG: any;
        $: any;
    }
}

const removeSettleIframe = () => {
    document.getElementById('SETTLE_IFRAME_DIV')?.remove();
    document.getElementById('SETTLE_LAYER_DIM')?.remove();
};

const SUCCESS_CODE = '0021';

const SUCCESS_CODES = new Set([SUCCESS_CODE]);

const extractOutStatCd = (raw: any): string | undefined => {
    return raw?.data?.amount?.outStatCd ?? undefined;
};

const isPaymentSuccess = (r?: PaymentResult | null) => !!r && !!extractOutStatCd(r) && SUCCESS_CODES.has(extractOutStatCd(r)!);

type VerifyFn = (ref: { mchtTrdNo?: string; paymentId?: string }) => Promise<PaymentResult | null>;

export const verifyPaymentFromServer: VerifyFn = async ({ mchtTrdNo }) => {
    if (!mchtTrdNo) return null;
    try {
        const raw: any = await mindUpHolidayApi.findPaymentResultByMchtTrdNo(mchtTrdNo);
        const unwrapped = raw;
        const outStatCd = extractOutStatCd(unwrapped);

        if (!outStatCd) return null;

        return { ...unwrapped, outStatCd } as PaymentResult;
    } catch {
        return null;
    }
};

async function pollPaymentResult(verify: VerifyFn, ref: { mchtTrdNo?: string; paymentId?: string }, opts: { tries?: number; intervalMs?: number } = {}): Promise<PaymentResult | null> {
    const tries = opts.tries ?? 5;
    const intervalMs = opts.intervalMs ?? 1500;

    for (let i = 0; i < tries; i++) {
        const res = await verify(ref);
        if (extractOutStatCd(res)) return res;
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
}

export const handleMessage = (token: string, diagnosisInfo: any, voucher: any, event: MessageEvent, navigate?: NavigateFunction, verifyPayment: VerifyFn = verifyPaymentFromServer) => {
    try {
        if (event.data == null) return;

        const allowedOrigins = [window.location.origin, 'https://npg.settlebank.co.kr', 'https://pg.settlebank.co.kr'];
        if (!allowedOrigins.includes(event.origin)) {
            return;
        }

        let payload: any = null;
        if (typeof event.data === 'string') {
            const s = event.data.trim();
            if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
                try {
                    payload = JSON.parse(s);
                } catch {
                    return;
                }
            } else return;
        } else if (typeof event.data === 'object') {
            payload = event.data;
        } else return;

        if (!payload || typeof payload !== 'object') return;

        if (payload.action === 'HECTO_IFRAME_CLOSE') {
            const result = payload.params?.result ?? 'unknown';
            const ref = {
                mchtTrdNo: payload.params?.mchtTrdNo as string | undefined,
                paymentId: payload.params?.paymentId as string | undefined,
            };

            removeSettleIframe();
            window.dispatchEvent(new CustomEvent('SETTLE_IFRAME_CLOSED', { detail: payload.params ?? { result } }));

            if (!navigate) return;

            (async () => {
                const serverResult = await pollPaymentResult(verifyPayment, ref);
                const success = isPaymentSuccess(serverResult);

                if (success) {
                    navigate(preciousChildPaths.counselReservation, {
                        replace: true,
                        state: { token, diagnosis: diagnosisInfo, voucher, payment: serverResult },
                    });
                } else {
                    navigate(connectMindPaths.sungnamPurchaseCounselService, {
                        replace: true,
                        state: { token, diagnosis: diagnosisInfo, voucher, payment: serverResult, reason: result },
                    });
                }
            })();
        }
    } catch (e) {
        console.error('[handleMessage] 예외 발생:', e);
    }
};

export const handlePayment = async (
    setLoading: (loading: boolean) => void,
    form: { [key: string]: string | number },
    _navigate: NavigateFunction,
    api?: (mchtTrdNo: string, paymentId: string) => void,
    verifyPayment: VerifyFn = verifyPaymentFromServer
) => {
    try {
        setLoading(true);

        const sendForm = { ...form };
        const requiredFields = ['productName', 'paymentAmount', 'serviceName', 'serviceType', 'customerName', 'notiUrl', 'method'];
        for (const field of requiredFields) {
            if (!sendForm[field as keyof typeof sendForm]) {
                alert(`${field} is required`);
                setLoading(false);
                return;
            }
        }

        const res = await fetch('https://api.tlchealthcare.co.kr/sms/payment/reqPayment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `${process.env.REACT_APP_AUTH_KEY}` },
            body: JSON.stringify(sendForm),
        });
        if (!res.ok) throw new Error('Server error');

        const data = await res.json();

        data.data.cancUrl = `${form.cancUrl}?cancel=true&mchtTrdNo=${data.data.mchtTrdNo}&paymentId=${data.data._id}`;
        data.data.nextUrl = `${form.nextUrl}?next=true&mchtTrdNo=${data.data.mchtTrdNo}&paymentId=${data.data._id}`;

        window.SETTLE_PG.pay(
            {
                ...data.data,
                ui: { type: 'iframe', width: '430', height: '660', position: 'center' },
            },
            (rsp: any) => {
                console.log('[SETTLE_PG.pay] rsp=', rsp);

                const ref = { mchtTrdNo: data.data.mchtTrdNo as string, paymentId: data.data._id as string };

                if (rsp?.result === 'next') {
                    (async () => {
                        const serverResult = await pollPaymentResult(verifyPayment, ref, { tries: 5, intervalMs: 1500 });
                        if (isPaymentSuccess(serverResult)) {
                            api && api(ref.mchtTrdNo, ref.paymentId);
                        } else {
                            removeSettleIframe();
                            window.dispatchEvent(new CustomEvent('SETTLE_IFRAME_CLOSED', { detail: { result: 'pending_or_fail' } }));
                            window.postMessage(
                                JSON.stringify({
                                    action: 'HECTO_IFRAME_CLOSE',
                                    params: { result: 'pending_or_fail', ...ref },
                                }),
                                window.location.origin
                            );
                        }
                    })();
                } else {
                    removeSettleIframe();
                    window.dispatchEvent(new CustomEvent('SETTLE_IFRAME_CLOSED', { detail: { result: rsp?.result ?? 'fail' } }));

                    window.postMessage(
                        JSON.stringify({
                            action: 'HECTO_IFRAME_CLOSE',
                            params: { result: rsp?.result ?? 'fail', ...ref },
                        }),
                        window.location.origin
                    );
                }
            }
        );
    } catch (err: any) {
        alert('Payment request error: ' + err.message);
    } finally {
        setLoading(false);
    }
};

export const handleIframe = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const mchtTrdNo = urlParams.get('mchtTrdNo') ?? undefined;
    const paymentId = urlParams.get('paymentId') ?? undefined;

    if (urlParams.get('cancel') === 'true') {
        if (window.parent !== window) {
            window.parent.postMessage(
                JSON.stringify({
                    action: 'HECTO_IFRAME_CLOSE',
                    params: { result: 'cancel', mchtTrdNo, paymentId },
                }),
                window.location.origin
            );
        }
    } else if (urlParams.get('next') === 'true') {
        if (window.parent !== window) {
            window.parent.postMessage(
                JSON.stringify({
                    action: 'HECTO_IFRAME_CLOSE',
                    params: { result: 'next', mchtTrdNo, paymentId },
                }),
                window.location.origin
            );
        }
    }
};
